import { Link } from 'react-router-dom'
import { assignmentSetupHref } from '../../lib/assignmentSetup.js'
import { teamDesk } from '../../lib/bowlCoverage.js'
import DeskPicker from './DeskPicker.jsx'
import styles from './repVolume.module.css'

/**
 * WHOSE DESK IS THIS TEAM ON — one cell, on every bowl-eligible team.
 *
 * Brian: "Every bowl-eligible team shows its assigned rep… Name the rep. Not
 * 'Assigned', not a blank, not a count."
 *
 * 🔴 IT NAMES SOMEBODY OR IT SAYS WHY IT CANNOT. There are five outcomes and
 * every one of them prints words a rep can act on:
 *
 *   school        the school record's own sr_assigned_rep — authoritative
 *   agree         that, and the conference breakdown agrees
 *   conflict      the two disagree, so BOTH are printed and neither wins
 *   coverage      conference coverage only, labelled as coverage
 *   none          nobody covers the body — a real hole, loud
 *   unresolvable  this board cannot read an owner — a missing field, quiet
 *
 * 🔴 WHY `conflict` PRINTS BOTH RATHER THAN PICKING. Brian: "Stored
 * sr_assigned_rep and what decide() returns have disagreed badly (TAPPS was
 * 118/118 wrong, UIL 33.2% vs 2.5%). If they differ on a team, show both
 * rather than silently picking one." A board that picks is a board that is
 * confidently wrong 118 times without ever showing its working.
 *
 * 🔴 AND NO DEAD-END FLAGS. Where nothing resolves, the cell carries a control
 * that goes somewhere real — the Governing Bodies board, which is the surface
 * where a body's coverage is actually set. It is not a button that opens a
 * modal saying "contact an administrator"; it is the destination.
 *
 * ⚠️ IT IS A READOUT AND A LINK, AND IT WRITES NOTHING. Assignment is a
 * governed write: `POST /api/champion-update` answers 403 `assignment_locked`
 * to a service identity because `cc_rep_assign` is false for automation —
 * automation proposes, a human with the grant applies. So this surface routes a
 * human to the place they can apply it rather than pretending to do it here.
 *
 * ⭐ ONE EXCEPTION SINCE 2026-10-26: on a SPLIT conference, a viewer who may
 * assign gets DeskPicker, which writes one row to the bowl board's own
 * append-only desk ledger (POST /api/fbs-bowl-eligibility/desk-pick). It still
 * writes no record: not sr_assigned_rep, not HubSpot, not ClickUp.
 */
export default function TeamDesk({ team, coverage = null, canPick = false, expectedVersion = null, season = null, onPicked = null }) {
  const desk = teamDesk(team, coverage)
  /* ⭐ THE SELECTOR (2026-10-26), only where the conference is split and only
     for a viewer whose role may pick. The server enforces both again. */
  const picker = canPick && desk.split ? (
    <DeskPicker
      school={team?.name || team?.team || ''}
      conference={team?.conference || ''}
      candidates={desk.candidates}
      current={desk.key === 'split' ? '' : (desk.reps?.[0] || '')}
      expectedVersion={expectedVersion} season={season}
      onPicked={onPicked}
    />
  ) : null

  if (desk.key === 'none' || desk.key === 'unresolvable') {
    const bad = desk.key === 'none'
    return (
      <span className={styles.deskCell}>
        <b className={bad ? styles.deskBad : styles.deskQuiet}>
          {bad ? 'No desk' : 'Not readable here'}
        </b>
        <span className={styles.deskWhy}>{desk.why}</span>
        {/* The working control. A real route, with the body it needs to land on
            named in the link text so the tap is predictable. */}
        <Link className={styles.deskAction} to={assignmentSetupHref(team?.conference, `/bowl-eligibility?${new URLSearchParams({ team: team?.name || team?.team || '' })}`)}>
          {bad ? 'Assign a desk on Governing Bodies' : 'Check the desk on Governing Bodies'}
        </Link>
      </span>
    )
  }

  return (
    <span className={styles.deskCell}>
      {/* The name. Plural when a conference is shared, and the pairing already
          applied by renderRep — "Philip Davanzo III + Madison Gill". */}
      <b className={styles.deskName}>{desk.reps.join(', ')}</b>

      {/* 🔴 THE SOURCE, ON THE FACE, NEVER IN A TOOLTIP. Brian: "Say which
          source you read." A name with no stated source is the same risk as a
          champion with no source URL — believable and unverifiable. */}
      <span className={`${styles.deskSrc} ${styles[`tone_${desk.tone}`]}`}>{desk.label}</span>
      {desk.key === 'team' && <span className={styles.deskWhy}>
        {desk.authorizedBy ? `Named by ${desk.authorizedBy}. ` : ''}
        {desk.recordOwner ? `CRM account owner: ${desk.recordOwner}. ` : ''}
        {desk.primaryRequired ? 'No primary was chosen. The CRM account owner is unchanged.' : 'Bowl coverage only; the CRM account owner is unchanged.'}
      </span>}

      {/* Both sides, when they disagree. The authoritative one is first and is
          marked as such; the derived one follows. */}
      {desk.key === 'conflict' && (
        <span className={styles.deskConflict}>
          School record says <b>{desk.reps.join(', ')}</b>; the 2026-2027 conference breakdown
          {desk.body ? ` for ${desk.body}` : ''} says <b>{desk.coverageReps.join(', ')}</b>.
          Nothing here picks between them.
        </span>
      )}

      {/* ⚠️ THE BODY NAME, NOT THE PARAGRAPH. The first cut printed the full
          `why` on every card — and because coverage is a statement about a
          CONFERENCE, that is the identical three-line paragraph repeated on all
          138 teams. Photographed at 390px it buried the rep's name under its
          own explanation, which is the opposite of dense detail collapsed
          behind a tap. The reason is general, so it is stated once in the
          section's own note; what is specific to this card is which body. */}
      {desk.body && desk.key === 'coverage' && (
        <span className={styles.deskWhy}>{desk.body}</span>
      )}

      {/* ⭐ WHO DECIDED, ON THE CARD. For a school-level desk the body name is
          the wrong line — there is no body; Notre Dame is an FBS Independent,
          which is precisely why it had no desk until a human named one. What
          is specific to this card is the PERSON WHO NAMED IT and when, which is
          also the only thing that makes the claim checkable. Same rule as a
          champion's source URL: a name with no stated authority is believable
          and unverifiable. One line, not the paragraph — the general reason is
          in the section note. */}
      {desk.key === 'assigned' && desk.authorizedBy && (
        <span className={styles.deskWhy}>
          Named by {desk.authorizedBy}
          {desk.authorizedAt ? `, ${desk.authorizedAt}` : ''}
        </span>
      )}

      {/* A PICK carries who made it, when, and why, on the card. */}
      {desk.key === 'picked' && (
        <span className={styles.deskWhy}>
          Picked by {desk.authorizedBy || 'an assignment manager'}
          {desk.authorizedAt ? `, ${desk.authorizedAt}` : ''}
          {desk.pickScope === 'conference' ? ' for the conference' : ''}
          {desk.pickReason ? `: ${desk.pickReason}` : ''}
        </span>
      )}

      {desk.key === 'split' && desk.body && (
        <span className={styles.deskWhy}>{desk.body}: nobody picked for this school yet</span>
      )}

      {picker}
    </span>
  )
}
